import { State } from '@meraki-internal/state';
import { IPendingCareMapInvite } from '../caremap/ICareMapInvite';
import { ICareMap, ICaremapAccess } from '../caremap/ICareMap';
import { CommunityModelFactory } from './CommunityModelFactory';
import { ICollaboratorPermission, ICollaborators } from './CommunityClient';
import { IFreeTierLimits } from '../innerhive-plus/revenue-cat/IFreeTierLimits';
import { CareMapState } from '../caremap/CareMapState';
import dayjs from 'dayjs';

export class CommunityModel extends State<Record<string, never>> {
    constructor(
        private caremap: ICareMap,
        private factory: CommunityModelFactory,
        private careMapState: CareMapState
    ) {
        super({ });
    };

    private initialDataLoaded: boolean = false;

    isLoaded = () => {
        return this.initialDataLoaded;
    };

    getInvites = (access: ICaremapAccess): IPendingCareMapInvite[] => {
        return this.invites.filter(i => i.access === access);
    };

    invites: IPendingCareMapInvite[] = [];

    private permissions: ICollaboratorPermission[] = [];
    private owner?: ICollaborators['owner'];
    private collaboratorCount: Number = 0;

    getOwner = (): ICollaborators['owner'] => {
        if (!this.owner){
            return {
                userId: this.caremap.userId,
                email: '',
                name: ''
            };
        }
        return this.owner;
    };

    private loadInvites = async () => {
        if (this.caremap.links.invites){
            const invites = await this.factory.client.getInvites(this.caremap);
            this.invites = invites;
        }
    };

    private loadCollaborators = async () => {
        if (this.caremap.links.permissions){
            const collaborators = await this.factory.client.getCollaborators(this.caremap);
            this.permissions = collaborators.permissions;
            this.owner = collaborators.owner;
            this.collaboratorCount = this.getViewers().length + this.getCoOwners().length;
        } else {
            this.collaboratorCount = (await this.factory.client.getCollaboratorCount(this.caremap)).count;
        }
    };

    load = async () => {
        await Promise.all([
            this.loadInvites(),
            this.loadCollaborators()
        ]);
        this.initialDataLoaded = true;
        this.setState({ });
    };

    // True when a realtime push targeting this caremap's permissions resource should reload the
    // access list. The server fires one event on the permissions href for any access-list change
    // (invite created/deleted/accepted/rejected, permission removed), so we reload both permissions
    // and invites off it — keeping the Sharing & Access list and member access banners live without
    // a full page reload.
    matchesAccessListResource = (resourceToRefresh: string): boolean => {
        const permissionsHref = this.caremap.links.permissions?.href;
        return Boolean(permissionsHref) && resourceToRefresh.toLowerCase() === permissionsHref!.toLowerCase();
    };

    deleteInvite = (token: string) => {
        const item: any = this.invites.find(i => i.token === token);

        if (!item) {
            throw new Error(`No invite found to delete for token ${token}`);
        }

        this.factory.client.delete(item);

        this.invites = this.invites.filter(i => i.token !== token);

        this.setState({ });
    };

    deleteCollaborator = (token: string) => {
        const item = this.permissions.find(i => i.invite.token === token);

        if (!item) {
            throw new Error(`No collaborator found to delete for token ${token}`);
        }

        this.factory.client.delete(item);
        this.permissions = this.permissions.filter(i => i.invite.token !== token);

        this.setState({});
    };

    getPermanentCoOwners = () => {
        return this.permissions.filter(p => p.access === 'co-owner');
    };

    getViewers = (): (ICollaboratorPermission | IPendingCareMapInvite)[] => {
        return [
            ...this.permissions.filter(p => p.access === 'view-only'),
            ...this.getInvites('view-only')
        ];
    };

    getCoOwners = (): (ICollaboratorPermission | IPendingCareMapInvite)[] => {
        return [
            ...this.permissions.filter(p => p.access === 'co-owner'),
            ...this.getInvites('co-owner')
        ];
    };

    // Counts pending invites AND accepted co-owners (getCoOwners), so a slot is reserved
    // at invite time — the recipient is never blocked at accept time. The owner doesn't count.
    addCoOwnerRequiresInnerhivePlus = (limits: IFreeTierLimits): boolean => {
        return this.getCoOwners().length >= limits.freeCoOwnersPerCaremap;
    };

    getCollaboratorCount = () => {
        return this.collaboratorCount;
    };

    getPermanentViewers = () => {
        return this.permissions.filter(p => p.access === 'view-only');
    };

    // Accepted collaborators' account emails (the address each accepted their invite with).
    // permissions is private, so expose the emails cleanly here rather than leaking the array.
    getAcceptedCollaboratorEmails = (): string[] => {
        return this.permissions
            .map(p => p.invite.acceptedBy.email)
            .filter((email): email is string => Boolean(email));
    };

    // True when saving a member with this contact email would auto-link it to an existing
    // account: the normalized email matches the owner's account email or an accepted
    // collaborator's. Mirrors the server's link rule (owner + accepted access-holders) so the
    // edit forms can warn before a save that would link. Empty email → false.
    emailWouldLink = (email: string): boolean => {
        const normalize = (value: string | undefined): string => (value || '').trim().toLowerCase();
        const target = normalize(email);
        if (!target) {
            return false;
        }
        if (normalize(this.getOwner().email) === target) {
            return true;
        }
        return this.getAcceptedCollaboratorEmails().some(collaboratorEmail => normalize(collaboratorEmail) === target);
    };

    // Reverse of the member→collaborator link: given a care-map member node, find the
    // collaborator (accepted permission or pending invite) it links to, so the member's
    // edit drawer can show an access banner. Undefined when not linked.
    // Name comes from the linked member's current care-map node (live), falling back
    // to the frozen invite name when the member no longer exists.
    //
    // Matches on durable identity, never on a member node id: node ids are regenerated on
    // delete-and-re-add, so a re-added member would lose the link. A still-pending invite
    // matches on the member's contact email vs intendedRecipient.email; an accepted
    // permission matches on the accepter's userId (via linkedUserId). We can't use the
    // invite email for accepted permissions: accepting with a different email than invited,
    // plus the accept-time rewrite of the member's contact email to the accepter's, makes
    // intendedRecipient.email stale.
    findCollaboratorForMember = (member: { memberId: string | undefined; email?: string; linkedUserId?: string }): { access: ICaremapAccess; name: string; isPending: boolean; token: string } | undefined => {
        const { memberId, email, linkedUserId } = member;

        // memberId is the member's OWN current node id, used only to resolve its live name.
        const memberLink = this.careMapState.maybeGetMemberLink(memberId);
        const liveName = memberLink ? this.careMapState.getMemberDisplayName(memberLink.member) : undefined;

        const normalize = (value: string | undefined): string => (value || '').trim().toLowerCase();
        const memberEmail = normalize(email);

        const permission = linkedUserId
            ? this.permissions.find(p => p.invite.acceptedBy.userId === linkedUserId)
            : undefined;
        if (permission) {
            return { access: permission.access, name: liveName || permission.invite.intendedRecipient.name || '', isPending: false, token: permission.invite.token };
        }
        const invite = memberEmail
            ? this.invites.find(i => normalize(i.intendedRecipient.email) === memberEmail)
            : undefined;
        if (invite) {
            return { access: invite.access, name: liveName || invite.intendedRecipient.name || '', isPending: true, token: invite.token };
        }
        return undefined;
    };

    isNewlyAdded = (invite: IPendingCareMapInvite): boolean => {
        return Boolean(invite.createdAt) && invite.createdAt > dayjs().add(-3, 'hours').toISOString();
    };

    canRemoveSelf = () => {
        const hasRemoveSelfLink = Boolean(this.caremap.links.removeSelf);
        return hasRemoveSelfLink && !this.factory.orgState.isOrgTeamMember();
    };

    isPermanentlyShared = () => {
        return this.getPermanentCoOwners().length || this.getPermanentViewers().length;
    };

    hasPendingInvites = () => {
        return this.invites.length > 0;
    };
};
